// =====================================================================
// supplier.js — SolarHub B2B Supplier Portal service layer
// Solo backend propio (`/hub-api/*`): único canal.
// =====================================================================

import { hubApiActivo, hubApiBaseUrl } from './hub';
import {
  PLATFORM_FEE_EQUIPMENT_PCT as FEE_EQUIP,
  TECH_EARNINGS_PCT as FEE_TECH,
  desgloseCierre,
} from './cierre';

// ---------- Commission model constants ----------
export const PLATFORM_FEE_EQUIPMENT_PCT = FEE_EQUIP;
export const TECH_EARNINGS_PCT = FEE_TECH;
export const SH_INSTALL_FEE_PCT = 20;

// ---------- Session storage ----------
export const SESSION_KEY = 'sh:supplier';

export function getSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.jwt || !parsed?.supplier) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveSession(jwt, supplier) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ jwt, supplier }));
  } catch {}
}

export function clearSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

let porElHub = true;

/** Solo para pruebas: rehabilita el intento contra el backend propio. */
export function reiniciarCanalProveedor() { porElHub = true; }

/**
 * POST al backend propio. Lanza si el canal no está o contestó mal. Único canal.
 */
async function porBackendPropio(path, cuerpo) {
  if (!porElHub || !hubApiActivo() || typeof fetch !== 'function') {
    throw new Error('Servicio backend no configurado.');
  }

  let res;
  let texto;
  try {
    res = await fetch(`${hubApiBaseUrl()}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cuerpo),
    });
    texto = await res.text();
  } catch (e) {
    porElHub = false;
    throw new Error(`Servicio (${path}) no disponible: ${e?.message || 'red'}`);
  }

  if (res.status === 502 || res.status === 503 || res.status === 504) {
    porElHub = false;
    throw new Error(`Servicio (${path}) no disponible: HTTP ${res.status}`);
  }

  let datos;
  try { datos = texto ? JSON.parse(texto) : {}; } catch (_) {
    porElHub = false;
    throw new Error(`Servicio (${path}) no disponible: el rewrite de /hub-api no está (llegó HTML del SPA)`);
  }

  if (!res.ok || datos.ok === false) {
    throw new Error(datos.message || datos.error || `HTTP ${res.status}`);
  }
  return { hayCanal: true, datos };
}

// ---------- Auth ----------
/**
 * Authenticate supplier.
 * credentials: { email, password } OR { token } (deep-link UUID)
 * On success saves session and returns { jwt, supplier }
 */
export async function supplierAuth(credentials) {
  const propio = await porBackendPropio('supplier-auth', credentials);
  saveSession(propio.datos.jwt, propio.datos.supplier);
  return propio.datos;
}

// ---------- Stock ----------
export async function getStock(supplierId, jwt) {
  const propio = await porBackendPropio('supplier-stock', { method: 'GET', supplier_id: supplierId, token: jwt });
  return propio.datos;
}

export async function createStockItem(item, jwt) {
  const propio = await porBackendPropio('supplier-stock', { method: 'POST', ...item, token: jwt });
  return propio.datos;
}

export async function updateStockItem(patch, jwt) {
  const propio = await porBackendPropio('supplier-stock', { method: 'PATCH', ...patch, token: jwt });
  return propio.datos;
}

// ---------- Purchase Orders ----------
export async function getOrders(supplierId, jwt, status = '') {
  const filtro = status && status !== 'all' ? { status } : {};
  const propio = await porBackendPropio('supplier-po', { method: 'GET', supplier_id: supplierId, token: jwt, ...filtro });
  return propio.datos;
}

/**
 * Update PO status.
 * extra: { tracking_code?, notes? }
 */
export async function updateOrderStatus(poId, status, extra = {}, jwt) {
  const propio = await porBackendPropio('supplier-po', { method: 'PATCH', po_id: poId, status, ...extra, token: jwt });
  return propio.datos;
}

// ---------- Analytics ----------
export async function getAnalytics(supplierId, jwt) {
  const propio = await porBackendPropio('supplier-analytics', { method: 'GET', supplier_id: supplierId, token: jwt });
  return propio.datos;
}

// ---------- Profile ----------
export async function updateProfile(profile, jwt) {
  const propio = await porBackendPropio('supplier-profile', { method: 'PATCH', ...profile, token: jwt });
  return propio.datos;
}

// ---------- Commission calculator ----------
/**
 * Calculate commission breakdown.
 * @param {number} subtotalEquipment  - Total equipment value (COP)
 * @param {number} installationTotal  - Total installation labor (COP)
 * @param {number} feePct             - Equipment fee % (default PLATFORM_FEE_EQUIPMENT_PCT)
 * @returns {{ supplierNet, platformFeeEquip, techEarnings, shInstallFee, totalPlatform }}
 */
export function calcCommission(preferencial, installationTotal, lista) {
  const d = desgloseCierre({
    lista: lista != null ? lista : preferencial,
    preferencial,
    instalacion: installationTotal,
  });
  return {
    supplierNet: d.supplierNet,
    platformFeeEquip: d.margen,
    techEarnings: d.techEarnings,
    shInstallFee: d.cargoAdminTec,
    totalPlatform: d.totalPlatform,
  };
}
